import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { debounce } from 'lodash'
import { route } from '~@/Composables/route'

export const COUNTS_IDLE = 'idle'
export const COUNTS_LOADING = 'loading'
export const COUNTS_LOADED = 'loaded'
export const COUNTS_FAILED = 'failed'

const DEBOUNCE_MS = 400

/**
 * How big the customers section of a create would be, counted server-side for the customer IDs currently entered
 * (CIO-14239). Shared by the section config modal (counts while typing) and the create modal (counts the applied
 * IDs), so the two agree.
 *
 * - **It owns its lifecycle.** It counts on mount and whenever `customerIds` or `enabled` change, and cancels on
 *   unmount, so a modal closed mid-count cannot have an answer land into a component that is going away. Call it
 *   synchronously in `setup`.
 * - **It debounces on request**, for a caller whose IDs come from free text. The count on mount is never debounced.
 * - **It ignores stale responses**, checked by sequence number, so a slow earlier request cannot overwrite a later one.
 *   Debouncing alone does not do that: it spaces requests out, it does not order their responses.
 * - **A failure is neither a pass nor a breach.** The verdict flags are only ever read off a server response, never
 *   re-derived from the numbers; a failed count leaves `counts` null and the caller lets the user submit (see
 *   GetSnapshotSectionCountsController for why).
 *
 * @param {() => Array<number>|null} customerIds the IDs to count for; null means the unfiltered whole tenant
 * @param {object} [options]
 * @param {() => boolean} [options.enabled] while false nothing is counted and the result is cleared
 * @param {boolean} [options.debounced] debounce the counts that follow a change
 */
export function useSnapshotSectionCounts (customerIds, { enabled = () => true, debounced = false } = {}) {
    /**
     * A client of our own, deliberately without the global axios interceptors — as `snapshotOperationStatus.js` does.
     *
     * Those interceptors (`Mixins/loadingState.js`) drive NProgress and the shared `isInLoadingState` counter, and
     * `DModal` renders its confirm button as `:disabled="isInLoadingState || disabledAction"` — so through
     * `window.axios` every count, the one on mount included, disabled the modal's own Confirm/Apply button for its
     * duration. Fixed here rather than in `DModal`. `create()` keeps the configured defaults (`X-Requested-With`
     * among them) but starts with no interceptors.
     */
    const client = window.axios.create()

    const status = ref(COUNTS_IDLE)
    const counts = ref(null)

    let sequence = 0

    // Only the answer for the IDs currently entered counts: while a newer count runs, `counts` still holds the previous
    // one, and a refusal or pass from that must not decide the submit button.
    const isRefused = computed(() => status.value === COUNTS_LOADED && counts.value?.isRefused === true)

    function request (ids) {
        sequence += 1
        const current = sequence

        status.value = COUNTS_LOADING

        client
            .post(route('tools.snapshot.section-counts'), { customer_ids: ids ?? null })
            .then((response) => {
                if (current !== sequence) {
                    return
                }

                counts.value = response.data
                status.value = COUNTS_LOADED
            })
            .catch((error) => {
                // Every failure shows as "unknown"; the console is where a 422 and a 500 stay distinguishable.
                console.error('Counting the customers section failed', error)

                if (current !== sequence) {
                    return
                }

                counts.value = null
                status.value = COUNTS_FAILED
            })
    }

    const debouncedRequest = debounce(request, DEBOUNCE_MS)

    function cancel () {
        debouncedRequest.cancel()

        // Nothing already in flight may land after this, or a cancelled count reappears as a result.
        sequence += 1
    }

    function refresh (immediate) {
        debouncedRequest.cancel()

        if (!enabled()) {
            cancel()
            counts.value = null
            status.value = COUNTS_IDLE

            return
        }

        if (immediate || !debounced) {
            request(customerIds())

            return
        }

        debouncedRequest(customerIds())
    }

    onMounted(() => refresh(true))
    watch(() => [enabled(), customerIds()], () => refresh(false), { deep: true })
    onBeforeUnmount(cancel)

    return {
        status,
        counts,
        isRefused,
    }
}
